import { useEffect, useMemo, useRef, useState } from "react";
import { Box, Tooltip, Typography } from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useNavigate } from "react-router-dom";
import API from "../api";
import { colors } from "../theme";
import { SidePanelSection } from "./SidePanel";
import { TYPE_COLOR } from "../scenes/tiresia-engine/tiresia/behaviors/constants";

// Anomalie comportamentali di Tires-IA (ultime 24h) mostrate dentro le viste
// Security: badge sulle righe delle tabelle e sezione nel drawer del log.

export const SEV_COLOR = {
  high: "#E24B4A", medium: "#eb7434", low: "#EF9F27", info: "#0d82b8",
};

const HOURS = 24;

// Sotto questa soglia di peak_score l'anomalia è rumore, a prescindere dalla severità
// (high/medium/low/info): non va mostrata né come badge né nel drawer. Il server filtra
// con "> soglia", il client ripete il controllo per i backend non aggiornati.
export const MIN_SCORE = 0.5;
const aboveMinScore = (a) => (a?.peak_score ?? 0) > MIN_SCORE;

export const SevBadge = ({ label }) => {
  const c = SEV_COLOR[label] ?? "#888888";
  return (
    <Box sx={{
      display: "inline-flex", px: 0.7, py: 0.15,
      bgcolor: `${c}22`, border: `1px solid ${c}`, borderRadius: 0.5,
    }}>
      <Typography sx={{ fontSize: 9, color: c, fontWeight: 700, lineHeight: 1.4 }}>
        {label?.toUpperCase() ?? "—"}
      </Typography>
    </Box>
  );
};

// Anomalie Tires-IA per gli IP distinti delle righe visibili, caricate in modo asincrono
// dopo la tabella: ip dell'host → { severity, count, score }. Il server risponde con una
// riga per IP (niente grafo). Gli IP già interrogati restano in cache: cambiando pagina
// si chiede solo il nuovo.
const IPS_PER_REQUEST = 150;

export const useTiresiaIpSeverity = (enabled, rows) => {
  const [byIp, setByIp] = useState(() => new Map());
  const [failedKey, setFailedKey] = useState("");
  const checked = useRef(new Set());

  // Chiave stabile degli IP sorgente distinti delle righe correnti.
  const ipsKey = useMemo(() => {
    if (!enabled) return "";
    const set = new Set();
    (rows ?? []).forEach((r) => {
      if (r?.src_ip) set.add(r.src_ip);
    });
    return [...set].sort().join(",");
  }, [enabled, rows]);

  useEffect(() => {
    if (!ipsKey) return undefined;
    const missing = ipsKey.split(",").filter((ip) => !checked.current.has(ip));
    if (!missing.length) return undefined;

    const ctrl = new AbortController();
    const chunks = [];
    for (let i = 0; i < missing.length; i += IPS_PER_REQUEST) chunks.push(missing.slice(i, i + IPS_PER_REQUEST));

    Promise.all(chunks.map((c) =>
      API.getTiresiaIpSummary({ hours: HOURS, ips: c.join(","), min_score: MIN_SCORE }, ctrl.signal)
    ))
      .then((responses) => {
        const found = new Map();
        responses.forEach((res) => {
          (res.data?.data ?? []).forEach((r) => {
            if (r?.ip && r.score > MIN_SCORE) found.set(r.ip, { severity: r.severity, count: r.count, score: r.score });
          });
        });
        missing.forEach((ip) => checked.current.add(ip));
        setByIp((prev) => new Map([...prev, ...found]));
      })
      .catch((err) => {
        if (err?.name === "CanceledError" || err?.code === "ERR_CANCELED") return;
        setFailedKey(ipsKey);
      });
    return () => ctrl.abort();
  }, [ipsKey]);

  // In caricamento finché c'è almeno un IP delle righe non ancora interrogato.
  const loading = Boolean(ipsKey)
    && failedKey !== ipsKey
    && ipsKey.split(",").some((ip) => !checked.current.has(ip));

  return { byIp, loading };
};

// Tutti gli IP con anomalie sopra soglia (per il filtro rapido "solo Tires-IA").
// null = disattivo o in caricamento.
export const useTiresiaAnomalousIps = (enabled) => {
  const [ips, setIps] = useState(null);

  useEffect(() => {
    if (!enabled) { setIps(null); return undefined; }
    const ctrl = new AbortController();
    API.getTiresiaIpSummary({ hours: HOURS, min_score: MIN_SCORE }, ctrl.signal)
      .then((res) => setIps((res.data?.data ?? []).filter((r) => r?.ip && r.score > MIN_SCORE).map((r) => r.ip)))
      .catch((err) => {
        if (err?.name === "CanceledError" || err?.code === "ERR_CANCELED") return;
        setIps([]);
      });
    return () => ctrl.abort();
  }, [enabled]);

  return ips;
};

// Info Tires-IA per una riga: la peggiore tra sorgente e destinazione.
export const lookupTiresiaRow = (byIp, row) => {
  const ip = row?.src_ip;
  return ip && byIp.has(ip) ? { ip, ...byIp.get(ip) } : null;
};

export const TiresiaRowBadge = ({ info }) => {
  if (!info) return null;
  const c = SEV_COLOR[info.severity] ?? "#888888";
  return (
    <Tooltip
      arrow
      title={`Tires-IA: ${info.count} anomal${info.count === 1 ? "y" : "ies"} with score above ${Math.round(MIN_SCORE * 100)}% in the last ${HOURS}h for ${info.ip} (worst: ${info.severity})`}
    >
      <Box sx={{
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        width: 24, height: 24, borderRadius: "6px",
        color: c, bgcolor: `${c}22`, border: `1px solid ${c}`,
      }}>
        <AutoAwesomeIcon sx={{ fontSize: 14 }} />
      </Box>
    </Tooltip>
  );
};

const analysisPath = () => sessionStorage.getItem("type") === "reseller" ? "/reseller/myC/tiresia-analysis" : "/user/tiresia-engine/tiresia-analysis";

// Un'anomalia attiva a cavallo del log (± questa finestra) può essere correlata: è solo un indizio, non una prova.
const CORRELATION_WINDOW_MS = 30 * 60 * 1000;
const toMs = (v) => { const raw = v?.$date ?? v; return raw ? new Date(raw).getTime() : NaN; };
const isNearLog = (a, log) => {
  const t = toMs(log?.timestamp), from = toMs(a?.first_seen), to = toMs(a?.last_seen);
  if ([t, from, to].some(Number.isNaN)) return false;
  return t >= from - CORRELATION_WINDOW_MS && t <= to + CORRELATION_WINDOW_MS;
};

const CorrelationChip = () => (
  <Tooltip arrow title="Tires-IA detected this behavior around the same period as this log, so the two may be related.">
    <Box sx={{ alignSelf: "flex-start", px: 0.8, py: 0.15, borderRadius: 0.5, bgcolor: "#EF9F2722", border: "1px solid #EF9F2788", cursor: "default" }}>
      <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#EF9F27" }}>Possible correlation</Typography>
    </Box>
  </Tooltip>
);

const SHOWN = 5;       // anomalie mostrate nel drawer
const MAX_CHIPS = 3;   // chip per riga (tipi / destinazioni), poi "+N"

// Chip come nella pagina Behaviours (colore per tipo, bordo semitrasparente).
const Chip = ({ label, color = "#aaaaaa", mono = false }) => (
  <Box sx={{ px: 0.6, py: 0.1, bgcolor: `${color}22`, border: `1px solid ${color}70`, borderRadius: 0.5 }}>
    <Typography sx={{ fontSize: 11, color, fontFamily: mono ? "monospace" : undefined }}>{label}</Typography>
  </Box>
);

const ChipRow = ({ label, items, colorOf, mono }) => {
  if (!items.length) return null;
  return (
    <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 0.5 }}>
      <Typography sx={{ fontSize: 11, color: colors.grey[500], minWidth: 64 }}>{label}</Typography>
      {items.slice(0, MAX_CHIPS).map((t) => <Chip key={t} label={t} color={colorOf(t)} mono={mono} />)}
      {items.length > MAX_CHIPS && (
        <Typography sx={{ fontSize: 11, color: colors.grey[500] }}>+{items.length - MAX_CHIPS}</Typography>
      )}
    </Box>
  );
};

// Sezione del drawer: anomalie Tires-IA (score sopra soglia) dell'host sorgente del log;
// per ciascuna tipi e destinazioni contattate.
export const TiresiaKnowsSection = ({ log }) => {
  const navigate = useNavigate();
  const [state, setState] = useState({ items: null, total: 0 }); // items null = loading
  const ipsKey = useMemo(
    () => log?.src_ip ?? "",
    [log?.src_ip]
  );

  useEffect(() => {
    if (!ipsKey) { setState({ items: [], total: 0 }); return undefined; }
    const ctrl = new AbortController();
    setState({ items: null, total: 0 });
    API.getTiresiaAnomalies({ hours: HOURS, entity_ips: ipsKey, min_score: MIN_SCORE, limit: SHOWN }, ctrl.signal)
      .then((res) => {
        const list = (res.data?.data ?? []).filter(aboveMinScore).sort(
          (a, b) => (b.peak_score ?? 0) - (a.peak_score ?? 0)
        );
        setState({ items: list.slice(0, SHOWN), total: res.data?.total ?? list.length });
      })
      .catch((err) => {
        if (err?.name === "CanceledError" || err?.code === "ERR_CANCELED") return;
        setState({ items: [], total: 0 });
      });
    return () => ctrl.abort();
  }, [ipsKey]);

  const { items, total } = state;
  const more = total - (items?.length ?? 0);

  // Apre l'analisi già filtrata: sorgente del log, score > 0.5, senza filtro porte.
  const openAnalysis = () => {
    const qs = new URLSearchParams({
      ips: ipsKey,
      peak_score: String(MIN_SCORE + 0.001),
      hours: String(HOURS),
      severity: "all",
      quick_ports: "0",
    });
    navigate(`${analysisPath()}?${qs}`, { state: { tab: "1" } });
  };

  return (
    <SidePanelSection
      title="What Tires-IA knows"
      action={
        <Box
          component="button"
          type="button"
          onClick={openAnalysis}
          sx={{
            p: 0, border: "none", background: "none", cursor: "pointer",
            display: "inline-flex", alignItems: "center", gap: 0.5,
            fontSize: 12, fontWeight: 600, color: "#5fb0b5", fontFamily: "inherit",
            "&:hover": { color: "#8fd0d4", textDecoration: "underline", textUnderlineOffset: 3 },
            "&:focus-visible": { outline: "1px solid #459095", outlineOffset: 2 },
          }}
        >
          Open analysis
          <OpenInNewIcon sx={{ fontSize: 13 }} />
        </Box>
      }
    >
      {items === null ? (
        <Typography sx={{ fontSize: 12.5, color: colors.grey[400], fontStyle: "italic" }}>
          Checking Tires-IA anomalies…
        </Typography>
      ) : items.length === 0 ? (
        <Typography sx={{ fontSize: 12.5, color: colors.grey[400] }}>
          No Tires-IA anomalies with score above {Math.round(MIN_SCORE * 100)}% for the source of this log in the last {HOURS}h.
        </Typography>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
          {items.map((a, i) => {
            const types = a.anomaly_types ?? [];
            const dsts = [...new Set((a.graph?.edges ?? []).map((e) => e.dst).filter(Boolean))];
            return (
              <Box key={a._id ?? i} sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <SevBadge label={a.severity_label} />
                  <Typography sx={{ fontSize: 12.5, fontFamily: "monospace", color: colors.grey[100], flexShrink: 0 }}>
                    {a.entity?.id ?? "—"}
                  </Typography>
                  <Typography sx={{ fontSize: 11.5, color: colors.grey[500], flex: 1, minWidth: 0 }} noWrap>
                    source of this log
                  </Typography>
                  {a.peak_score != null && (
                    <Typography sx={{ fontSize: 11.5, color: SEV_COLOR[a.severity_label] ?? colors.grey[400], fontFamily: "monospace" }}>
                      {Math.round(a.peak_score * 100)}%
                    </Typography>
                  )}
                </Box>
                {isNearLog(a, log) && <CorrelationChip />}
                <ChipRow label="Anomalies" items={types} colorOf={(t) => TYPE_COLOR[t] ?? "#aaaaaa"} />
                <ChipRow label="Talks to" items={dsts} colorOf={() => "#7eadbb"} mono />
              </Box>
            );
          })}
          {more > 0 && (
            <Typography sx={{ fontSize: 11.5, color: colors.grey[500] }}>+{more} more</Typography>
          )}
        </Box>
      )}
    </SidePanelSection>
  );
};
